import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Slider,
  FormControlLabel,
  Switch,
  Drawer,
  IconButton,
  Autocomplete,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Clear,
  Star,
  Search,
  FilterList,
  Close,
  CheckCircle,
  Refresh,
  Work,
  LocationOn,
  Schedule,
  School,
  Business,
  Map,
  Gavel,
  LocalOffer,
  ShoppingCart,
  AutoAwesome,
  Image,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { UserService, UserSearchResult } from '../../services/userService';
import { Category } from '../../lib/supabase';
import { useAppMode } from '../../contexts/AppModeContext';
import MapSearchNew from '../MapSearch/MapSearchNew';
import AISearchWithImageModal from '../AISearchWithImageModal';
import { useTranslation } from '../../contexts/TranslationContext';
import { translateBatch } from '../../services/translationService';

interface FilterSidebarProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: any) => void;
  onClearFilters: () => void;
  isJobMode?: boolean; // Add prop to determine if we're in job mode
  // Current filter states
  searchQuery: string;
  selectedCategory: string;
  sortBy: string;
  priceRange: number[];
  salaryType?: 'any' | 'hourly' | 'fixed' | 'negotiable';
  purchaseType?: 'any' | 'direct' | 'negotiable' | 'auction' | 'offer';
  selectedCondition: string;
  showOnlyInStock: boolean;
  showOnlyOnSale: boolean;
  excludeOwnProducts: boolean;
  selectedBrands: Set<string>;
  minRating: number;
  selectedSeller: UserSearchResult | null;
  sellerSearchQuery: string;
  requiredSkills: string[];
  // Tag filter
  selectedTags: string[];
  // Custom filter fields
  customFilter1: string;
  customFilter2: string;
  customFilter3: string;
  customFilter4: string;
  customFilter5: string;
  // Filter state setters
  setSearchQuery: (value: string) => void;
  setSelectedCategory: (value: string) => void;
  setSortBy: (value: string) => void;
  setPriceRange: (value: number[]) => void;
  setSalaryType?: (value: 'any' | 'hourly' | 'fixed' | 'negotiable') => void;
  setPurchaseType?: (value: 'any' | 'direct' | 'negotiable' | 'auction' | 'offer') => void;
  setSelectedCondition: (value: string) => void;
  setShowOnlyInStock: (value: boolean) => void;
  setShowOnlyOnSale: (value: boolean) => void;
  setExcludeOwnProducts: (value: boolean) => void;
  setSelectedBrands: (value: Set<string>) => void;
  setMinRating: (value: number) => void;
  setSelectedSeller: (value: UserSearchResult | null) => void;
  setSellerSearchQuery: (value: string) => void;
  setRequiredSkills: (value: string[]) => void;
  setSelectedTags: (value: string[]) => void;
  setCustomFilter1: (value: string) => void;
  setCustomFilter2: (value: string) => void;
  setCustomFilter3: (value: string) => void;
  setCustomFilter4: (value: string) => void;
  setCustomFilter5: (value: string) => void;
  // Data
  brands: string[];
  categories: Category[];
  loadingCategories: boolean;
  categoriesError: string | null;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  open,
  onClose,
  onApplyFilters,
  onClearFilters,
  isJobMode = false, // Default to false (shop mode)
  // Current states
  searchQuery,
  selectedCategory,
  sortBy,
  priceRange,
  salaryType = 'any',
  purchaseType = 'any',
  selectedCondition,
  showOnlyInStock,
  showOnlyOnSale,
  excludeOwnProducts,
  selectedBrands,
  minRating,
  selectedSeller,
  sellerSearchQuery,
  requiredSkills,
  selectedTags,
  customFilter1,
  customFilter2,
  customFilter3,
  customFilter4,
  customFilter5,
  // Setters
  setSearchQuery,
  setSelectedCategory,
  setSortBy,
  setPriceRange,
  setSalaryType,
  setPurchaseType,
  setSelectedCondition,
  setShowOnlyInStock,
  setShowOnlyOnSale,
  setExcludeOwnProducts,
  setSelectedBrands,
  setMinRating,
  setSelectedSeller,
  setSellerSearchQuery,
  setRequiredSkills,
  setSelectedTags,
  setCustomFilter1,
  setCustomFilter2,
  setCustomFilter3,
  setCustomFilter4,
  setCustomFilter5,
  // Data
  brands,
  categories,
  loadingCategories,
  categoriesError,
}) => {
  const { t, language } = useTranslation();
  const theme = useTheme();
  // Local state for brands popup
  const [brandsPopupOpen, setBrandsPopupOpen] = useState(false);

  // Local state for editing filters
  const [localRequiredSkills, setLocalRequiredSkills] = useState<string[]>([]);
  const [localSelectedTags, setLocalSelectedTags] = useState<string[]>([]);
  const [tagInputValue, setTagInputValue] = useState('');

  // Local state for map search
  const [mapSearchOpen, setMapSearchOpen] = useState(false);

  // Translated category names cache per current language
  const [translatedCategories, setTranslatedCategories] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!categories || categories.length === 0) {
          setTranslatedCategories({});
          return;
        }
        if (language === 'en') {
          // Reset to original when English selected
          const map: Record<string, string> = {};
          categories.forEach(c => { map[c.id] = c.name; });
          if (!cancelled) setTranslatedCategories(map);
          return;
        }
        const names = categories.map(c => c.name);
        const translated = await translateBatch(names, language as any, 'en');
        const map: Record<string, string> = {};
        categories.forEach((c, idx) => { map[c.id] = translated[idx] || c.name; });
        if (!cancelled) setTranslatedCategories(map);
      } catch (_) {
        // Fallback to original names on any error
        const map: Record<string, string> = {};
        categories.forEach(c => { map[c.id] = c.name; });
        if (!cancelled) setTranslatedCategories(map);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [categories, language]);
  const [selectedMapLocations, setSelectedMapLocations] = useState<string[]>([]);
  const [aiSearchOpen, setAiSearchOpen] = useState(false);

  // Sync local state with parent state when sidebar opens
  useEffect(() => {
    if (open) {
      // Initialize local state with current parent state
      setLocalRequiredSkills(requiredSkills || []);
      setLocalSelectedTags(selectedTags || []);
      console.log('FilterSidebar opened with requiredSkills:', requiredSkills);
      console.log('FilterSidebar opened with selectedTags:', selectedTags);
    }
  }, [open, requiredSkills, selectedTags]);

  const { is_job } = useAppMode();

  const handleBrandToggle = (brand: string) => {
    const newBrands = new Set(selectedBrands);
    if (newBrands.has(brand)) {
      newBrands.delete(brand);
    } else {
      newBrands.add(brand);
    }
    setSelectedBrands(newBrands);
  };

  // Tag handling functions
  const handleTagInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setTagInputValue(value);
    
    // Check if comma was entered
    if (value.includes(',')) {
      const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
      if (tags.length > 0) {
        const newTags = [...localSelectedTags, ...tags];
        setLocalSelectedTags(newTags);
        setSelectedTags(newTags);
        setTagInputValue(''); // Clear input
      }
    }
  };

  const handleTagInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && tagInputValue.trim()) {
      const newTags = [...localSelectedTags, tagInputValue.trim()];
      setLocalSelectedTags(newTags);
      setSelectedTags(newTags);
      setTagInputValue('');
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    const newTags = localSelectedTags.filter(tag => tag !== tagToRemove);
    setLocalSelectedTags(newTags);
    setSelectedTags(newTags);
  };

  const conditions = [
    { value: 'all', label: t('filterSidebar.condition.all') },
    { value: 'new', label: t('filterSidebar.condition.new') },
    { value: 'used', label: t('filterSidebar.condition.used') },
    { value: 'refurbished', label: t('filterSidebar.condition.refurbished') }
  ];

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 380,
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: theme.shadows[10],
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 85%)`, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.10),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              }}
            >
              <FilterList sx={{ fontSize: 22, color: theme.palette.primary.main }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" sx={{ 
              color: 'text.primary',
            }}>
              Smart Filters
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'text.secondary',
              background: 'transparent',
              '&:hover': { 
                background: alpha(theme.palette.text.primary, 0.06),
                color: 'text.primary',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Filter Content */}
      <Box sx={{ 
        p: 3, 
        height: '100%', 
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: alpha(theme.palette.text.primary, 0.06),
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.text.primary, 0.18),
          borderRadius: '3px',
          '&:hover': {
            background: alpha(theme.palette.text.primary, 0.28),
          },
        },
      }}>
        {/* Search Query */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            background: alpha(theme.palette.primary.main, 0.05), 
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Search sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {is_job ? t('filterSidebar.search.title.jobs') : t('filterSidebar.search.title.products')}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={is_job ? t('allProducts.search.placeholder.jobs') : t('allProducts.search.placeholder.products')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.text.secondary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(0,0,0,0.6)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}
            />
          </Paper>
        </motion.div>

        {/* Purchase Type (Shop only) */}
        {!is_job && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
          >
            <Paper sx={{ 
              p: 2, 
              mb: 3, 
              background: alpha(theme.palette.primary.main, 0.05), 
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Gavel sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                  {t('filterSidebar.purchaseType.title')}
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                value={purchaseType}
                onChange={(_, val) => {
                  setPurchaseType && setPurchaseType((val ?? 'any') as any);
                }}
                fullWidth
                sx={{
                  p: 0.75,
                  gap: 1,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  alignItems: 'stretch',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    minHeight: 46,
                    px: 1.5,
                    textTransform: 'none',
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    background: theme.palette.background.paper,
                    boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.06)}`,
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease',
                    width: '100%',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.08)}`,
                      background: alpha(theme.palette.primary.main, 0.04),
                    },
                  },
                  '& .MuiToggleButtonGroup-grouped.Mui-selected': {
                    color: theme.palette.primary.contrastText,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                    boxShadow: `0 14px 30px ${alpha(theme.palette.primary.main, 0.28)}`,
                    transform: 'translateY(-1px)',
                  },
                  '& .MuiToggleButtonGroup-grouped.Mui-selected:hover': {
                    background: theme.palette.primary.dark,
                  },
                }}
              >
                <ToggleButton value="direct" aria-label={t('filterSidebar.purchaseType.direct')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
                    <ShoppingCart fontSize="small" />
                    <span>{t('filterSidebar.purchaseType.direct')}</span>
                  </Box>
                </ToggleButton>
                <ToggleButton value="negotiable" aria-label={t('filterSidebar.purchaseType.negotiable')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
                    <Gavel fontSize="small" />
                    <span>{t('filterSidebar.purchaseType.negotiable')}</span>
                  </Box>
                </ToggleButton>
                <ToggleButton value="auction" aria-label={t('filterSidebar.purchaseType.auctions')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
                    <Gavel fontSize="small" />
                    <span>{t('filterSidebar.purchaseType.auctions')}</span>
                  </Box>
                </ToggleButton>
                <ToggleButton value="offer" aria-label={t('filterSidebar.purchaseType.offer')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
                    <LocalOffer fontSize="small" />
                    <span>{t('filterSidebar.purchaseType.offer')}</span>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          </motion.div>
        )}

        {/* Job-related filters - only show in job mode */}
        {is_job && (
          <>
            {/* Job Type */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Paper sx={{ 
                p: 2, 
                mb: 3, 
                background: alpha(theme.palette.primary.main, 0.05), 
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Work sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    {t('allProducts.filters.category')}
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={customFilter1 ? customFilter1.split(',').map(v => v.trim()).filter(v => v) : []}
                    onChange={(e) => {
                      const selectedValues = e.target.value as string[];
                      const newValue = selectedValues.join(', ');
                      setCustomFilter1(newValue);
                    }}
                    displayEmpty
                    sx={{
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.divider}`,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.secondary,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      }
                    }}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: theme.palette.text.secondary }}>Select job types...</Typography>;
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              sx={{
                                background: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                border: 'none',
                                fontWeight: 'bold',
                                '& .MuiChip-deleteIcon': {
                                  color: theme.palette.primary.contrastText,
                                }
                              }}
                            />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="Full-time">Full-time</MenuItem>
                    <MenuItem value="Part-time">Part-time</MenuItem>
                    <MenuItem value="Contract">Contract</MenuItem>
                    <MenuItem value="Freelance">Freelance</MenuItem>
                    <MenuItem value="Internship">Internship</MenuItem>
                    <MenuItem value="Temporary">Temporary</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            </motion.div>

            {/* Experience Level */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Paper sx={{ 
                p: 2, 
                mb: 3, 
                background: alpha(theme.palette.primary.main, 0.05), 
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    Experience Level
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={customFilter2 ? customFilter2.split(',').map(v => v.trim()).filter(v => v) : []}
                    onChange={(e) => {
                      const selectedValues = e.target.value as string[];
                      const newValue = selectedValues.join(', ');
                      setCustomFilter2(newValue);
                    }}
                    displayEmpty
                    sx={{
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.divider}`,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.secondary,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      }
                    }}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: theme.palette.text.secondary }}>Select experience levels...</Typography>;
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              sx={{
                                background: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                border: 'none',
                                fontWeight: 'bold',
                                '& .MuiChip-deleteIcon': {
                                  color: theme.palette.primary.contrastText,
                                }
                              }}
                            />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="Any Experience">Any Experience</MenuItem>
                    <MenuItem value="Entry-level">Entry-level</MenuItem>
                    <MenuItem value="Mid-level">Mid-level</MenuItem>
                    <MenuItem value="Senior-level">Senior-level</MenuItem>
                    <MenuItem value="Executive">Executive</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            </motion.div>

            {/* Required Skills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <Paper sx={{ 
                p: 2, 
                mb: 3, 
                background: alpha(theme.palette.primary.main, 0.05), 
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Schedule sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    Required Skills
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter skills separated by commas (e.g., React, JavaScript, Node.js)..."
                  value={customFilter3}
                  onChange={(e) => setCustomFilter3(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.text.secondary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main,
                    }
                  }}
                />
              </Paper>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Paper sx={{ 
                p: 2, 
                mb: 3, 
                background: alpha(theme.palette.primary.main, 0.05), 
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <School sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    Benefits
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter benefits separated by commas (e.g., Health Insurance, Remote Work, 401k)..."
                  value={customFilter4}
                  onChange={(e) => setCustomFilter4(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.text.secondary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main,
                    }
                  }}
                />
              </Paper>
            </motion.div>

            {/* City/Location */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <Paper sx={{ 
                p: 2, 
                mb: 3, 
                background: alpha(theme.palette.primary.main, 0.05), 
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Business sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    City/Location
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter cities/locations separated by commas (e.g., New York, San Francisco, Remote)..."
                  value={customFilter5}
                  onChange={(e) => setCustomFilter5(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.text.secondary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main,
                    }
                  }}
                />
              </Paper>
            </motion.div>
          </>
        )}

        {/* Search On Map Button - available in both modes */}
        {
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Paper sx={{ 
              p: 2, 
              mb: 3, 
              background: alpha(theme.palette.primary.main, 0.05), 
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Map />}
                onClick={() => setMapSearchOpen(true)}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  borderRadius: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: `0 12px 26px ${alpha(theme.palette.primary.main, 0.22)}`,
                  '&:hover': {
                    background: theme.palette.primary.dark,
                    boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.30)}`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {t('allProducts.searchOnMap')}
              </Button>
              {selectedMapLocations.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('filterSidebar.map.selectedLocations')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedMapLocations.map((location, index) => (
                      <Chip
                        key={index}
                        label={location}
                        onDelete={() => {
                          const newLocations = selectedMapLocations.filter((_, i) => i !== index);
                          setSelectedMapLocations(newLocations);
                          // Update the customFilter5 with remaining locations
                          const newValue = newLocations.join(', ');
                          setCustomFilter5(newValue);
                        }}
                        sx={{
                          background: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          '& .MuiChip-deleteIcon': {
                            color: theme.palette.primary.contrastText,
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </motion.div>
        }

        {/* AI Search with Image - Shop mode only */}
        {!is_job && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Paper sx={{ 
              p: 2, 
              mb: 3, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
              boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                }}>
                  <AutoAwesome sx={{ color: 'white', fontSize: 18 }} />
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ 
                  color: theme.palette.text.primary,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {t('filterSidebar.aiSearch.title')}
                </Typography>
              </Box>
              
              <Button
                fullWidth
                variant="contained"
                startIcon={<Image />}
                onClick={() => setAiSearchOpen(true)}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  borderRadius: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: `0 12px 26px ${alpha(theme.palette.primary.main, 0.28)}`,
                  '&:hover': {
                    background: theme.palette.primary.dark,
                    boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.34)}`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {t('filterSidebar.aiSearch.findSimilar')}
              </Button>
              
              <Typography variant="body2" sx={{ 
                color: theme.palette.text.secondary,
                mt: 1,
                textAlign: 'center',
                fontStyle: 'italic',
              }}>
                {t('filterSidebar.aiSearch.uploadImage')}
              </Typography>
            </Paper>
          </motion.div>
        )}

        {/* Filter by Tags - Shop mode only */}
        {!is_job && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Paper sx={{ 
              p: 2, 
              mb: 3, 
              background: alpha(theme.palette.primary.main, 0.05), 
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.20)}`,
                }}>
                  <Typography sx={{ 
                    color: 'white', 
                    fontSize: 16, 
                    fontWeight: 'bold' 
                  }}>
                    🏷️
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ 
                  color: theme.palette.text.primary,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {t('filterSidebar.tags.title')}
                </Typography>
              </Box>
              
              {/* Tag Input */}
              <TextField
                fullWidth
                placeholder={t('filterSidebar.tags.placeholder')}
                value={tagInputValue}
                onChange={handleTagInputChange}
                onKeyPress={handleTagInputKeyPress}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: theme.palette.background.paper,
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.text.secondary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2,
                    },
                  },
                }}
              />
              
              {/* Selected Tags Display */}
              {localSelectedTags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 1 
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                    }}>
                      {t('filterSidebar.tags.selectedTags')} ({localSelectedTags.length}):
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setLocalSelectedTags([]);
                        setSelectedTags([]);
                      }}
                      sx={{
                        borderColor: theme.palette.error.main,
                        color: theme.palette.error.main,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        px: 1.5,
                        py: 0.3,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        background: alpha(theme.palette.error.main, 0.06),
                        border: `1px solid ${theme.palette.error.main}`,
                        minWidth: 'auto',
                        '&:hover': {
                          borderColor: theme.palette.error.dark,
                          backgroundColor: alpha(theme.palette.error.main, 0.10),
                          transform: 'translateY(-1px)',
                          boxShadow: `0 10px 18px ${alpha(theme.palette.error.main, 0.18)}`,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {t('filterSidebar.tags.clearAll')}
                    </Button>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1 
                  }}>
                    {localSelectedTags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleTagRemove(tag)}
                        sx={{
                          background: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          fontWeight: 600,
                          '& .MuiChip-deleteIcon': {
                            color: theme.palette.primary.contrastText,
                            '&:hover': {
                              color: alpha(theme.palette.primary.contrastText, 0.85),
                            },
                          },
                          '&:hover': {
                            background: theme.palette.primary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 10px 18px ${alpha(theme.palette.primary.main, 0.22)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              <Typography variant="body2" sx={{ 
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
                fontSize: '0.875rem',
              }}>
                {t('filterSidebar.tags.tip')}
              </Typography>
            </Paper>
          </motion.div>
        )}

        {/* Price Range */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            background: alpha(theme.palette.primary.main, 0.05), 
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {is_job ? '💼 ' + t('filterSidebar.priceRange.salary') : '💰 ' + t('filterSidebar.priceRange.price')}
              </Typography>
            </Box>
            
            {/* Beautiful Slider */}
            <Box sx={{ mb: 3 }}>
              <Slider
                value={priceRange}
                onChange={(_, newValue) => setPriceRange(newValue as number[])}
                valueLabelDisplay="auto"
                min={0}
                max={is_job ? 300000 : 2000}
                step={is_job ? 1000 : 10}
                valueLabelFormat={(value) => `$${value.toLocaleString()}`}
                sx={{ 
                  mb: 2,
                  '& .MuiSlider-thumb': {
                    background: theme.palette.primary.main,
                    boxShadow: `0 10px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                    width: 20,
                    height: 20,
                    '&:hover': {
                      boxShadow: `0 14px 24px ${alpha(theme.palette.primary.main, 0.32)}`,
                      transform: 'scale(1.1)',
                    },
                    '&.Mui-active': {
                      boxShadow: `0 18px 32px ${alpha(theme.palette.primary.main, 0.40)}`,
                      transform: 'scale(1.2)',
                    },
                    transition: 'all 0.2s ease',
                  },
                  '& .MuiSlider-track': {
                    background: theme.palette.primary.main,
                    height: 6,
                    borderRadius: 3,
                  },
                  '& .MuiSlider-rail': {
                    background: alpha(theme.palette.primary.main, 0.14),
                    height: 6,
                    borderRadius: 3,
                  },
                  '& .MuiSlider-valueLabel': {
                    background: theme.palette.primary.main,
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    '&:before': {
                      borderBottomColor: theme.palette.primary.main,
                    }
                  }
                }}
              />
            </Box>

            {/* Price Input Fields */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {/* Minimum Price Input */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                  {is_job ? t('filterSidebar.priceRange.minSalary') : t('filterSidebar.priceRange.minPrice')}
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  placeholder={is_job ? t('filterSidebar.priceRange.minSalaryPlaceholder') : t('filterSidebar.priceRange.minPricePlaceholder')}
                  value={priceRange[0] === 0 ? '' : priceRange[0]}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0);
                    const maxValue = is_job ? 300000 : 2000;
                    setPriceRange([value, Math.min(priceRange[1], maxValue)]);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                        transition: 'all 0.2s ease',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.text.secondary,
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.10)}`,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}`,
                      }
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'right',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }
                  }}
                />
              </Box>

              {/* Maximum Price Input */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                  {is_job ? t('filterSidebar.priceRange.maxSalary') : t('filterSidebar.priceRange.maxPrice')}
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  placeholder={is_job ? t('filterSidebar.priceRange.maxSalaryPlaceholder') : t('filterSidebar.priceRange.maxPricePlaceholder')}
                  value={priceRange[1] === (is_job ? 300000 : 2000) ? '' : priceRange[1]}
                  onChange={(e) => {
                    const maxValue = is_job ? 300000 : 2000;
                    const value = e.target.value === '' ? maxValue : Math.min(maxValue, Math.max(priceRange[0], parseInt(e.target.value) || maxValue));
                    setPriceRange([priceRange[0], value]);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                        transition: 'all 0.2s ease',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.text.secondary,
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.10)}`,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}`,
                      }
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'right',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Price Range Display */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 1.5,
              background: alpha(theme.palette.primary.main, 0.06),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              mb: is_job ? 2 : 0 
            }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                ${priceRange[0].toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                to
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                ${priceRange[1].toLocaleString()}
              </Typography>
            </Box>

            {/* Quick Price Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: is_job ? 2 : 0 }}>
              {is_job ? (
                // Job salary quick buttons
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPriceRange([0, 50000])}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    {t('filterSidebar.priceRange.quickButtons.under50k')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPriceRange([50000, 100000])}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    {t('filterSidebar.priceRange.quickButtons.50k100k')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPriceRange([100000, 200000])}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    {t('filterSidebar.priceRange.quickButtons.100k200k')}
                  </Button>
                </>
              ) : (
                // Product price quick buttons
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPriceRange([0, 50])}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    {t('filterSidebar.priceRange.quickButtons.under50')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPriceRange([50, 200])}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    {t('filterSidebar.priceRange.quickButtons.50to200')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPriceRange([200, 500])}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    {t('filterSidebar.priceRange.quickButtons.200to500')}
                  </Button>
                </>
              )}
            </Box>

            {is_job && (
              <FormControl fullWidth size="small">
                <InputLabel id="salary-type-label">{t('filterSidebar.salaryType.title')}</InputLabel>
                <Select labelId="salary-type-label" label={t('filterSidebar.salaryType.title')} defaultValue="" sx={{ background: theme.palette.background.paper, borderRadius: 2 }}>
                  <MenuItem value="">{t('filterSidebar.salaryType.any')}</MenuItem>
                  <MenuItem value="hourly">{t('filterSidebar.salaryType.hourly')}</MenuItem>
                  <MenuItem value="fixed">{t('filterSidebar.salaryType.fixed')}</MenuItem>
                  <MenuItem value="negotiable">{t('filterSidebar.salaryType.negotiable')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Paper>
        </motion.div>

        {/* Category */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            background: alpha(theme.palette.primary.main, 0.05), 
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {is_job ? `🧭 ${t('filterSidebar.category.title.jobs')}` : `📂 ${t('filterSidebar.category.title.products')}`}
              </Typography>
            </Box>
            
            <FormControl fullWidth size="small">
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={loadingCategories}
                error={!!categoriesError}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.secondary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  }
                }}
              >
                <MenuItem value="All Categories">{is_job ? t('filterSidebar.category.allJobCategories') : t('allProducts.allCategories')}</MenuItem>
                {loadingCategories ? (
                  <MenuItem disabled>{t('allProducts.loadingCategories')}</MenuItem>
                ) : categoriesError ? (
                  <MenuItem disabled>{t('allProducts.errorLoadingCategories')}</MenuItem>
                ) : (
                  categories.map(category => (
                    <MenuItem key={category.id} value={category.name}>
                      {translatedCategories[category.id] || category.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {categoriesError && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  // Retry logic would go here
                }}
                sx={{ 
                  mt: 1,
                  color: 'text.primary',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    background: alpha(theme.palette.primary.main, 0.06),
                  }
                }}
                fullWidth
                startIcon={<Refresh />}
              >
                {t('home.categories.retry')}
              </Button>
            )}
          </Paper>
        </motion.div>

        {/* Brands */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            background: alpha(theme.palette.primary.main, 0.05), 
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {is_job ? `🏢 ${t('filterSidebar.brands.title.jobs')}` : `🏷️ ${t('filterSidebar.brands.title.products')}`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setBrandsPopupOpen(true)}
              sx={{
                py: 1.5,
                borderColor: theme.palette.divider,
                color: 'text.primary',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.06),
                }
              }}
            >
              {selectedBrands.size > 0 
                ? `${selectedBrands.size} ${is_job ? t('filterSidebar.brands.company') : t('filterSidebar.brands.brand')}${selectedBrands.size > 1 ? 's' : ''} ${t('filterSidebar.brands.selected')}`
                : is_job ? t('filterSidebar.brands.selectCompanies') : t('filterSidebar.brands.selectBrands')
              }
            </Button>
            {selectedBrands.size > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Array.from(selectedBrands).map(brand => (
                  <Chip
                    key={brand}
                    label={brand}
                    onDelete={() => handleBrandToggle(brand)}
                    size="small"
                    sx={{
                      background: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.primary.contrastText,
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </motion.div>

        {/* Condition / Job specifics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            background: alpha(theme.palette.primary.main, 0.05), 
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}>
            {is_job ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="job-type-label">{t('filterSidebar.jobs.type.title')}</InputLabel>
                  <Select labelId="job-type-label" label={t('filterSidebar.jobs.type.title')} defaultValue="" sx={{ background: theme.palette.background.paper, borderRadius: 2 }}>
                    <MenuItem value="">{t('filterSidebar.jobs.type.any')}</MenuItem>
                    <MenuItem value="full-time">{t('filterSidebar.jobs.type.fullTime')}</MenuItem>
                    <MenuItem value="part-time">{t('filterSidebar.jobs.type.partTime')}</MenuItem>
                    <MenuItem value="contract">{t('filterSidebar.jobs.type.contract')}</MenuItem>
                    <MenuItem value="freelance">{t('filterSidebar.jobs.type.freelance')}</MenuItem>
                    <MenuItem value="internship">{t('filterSidebar.jobs.type.internship')}</MenuItem>
                    <MenuItem value="temporary">{t('filterSidebar.jobs.type.temporary')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="exp-level-label">{t('filterSidebar.jobs.experience.title')}</InputLabel>
                  <Select labelId="exp-level-label" label={t('filterSidebar.jobs.experience.title')} defaultValue="" sx={{ background: theme.palette.background.paper, borderRadius: 2 }}>
                    <MenuItem value="">{t('filterSidebar.jobs.experience.any')}</MenuItem>
                    <MenuItem value="entry-level">{t('filterSidebar.jobs.experience.entry')}</MenuItem>
                    <MenuItem value="mid-level">{t('filterSidebar.jobs.experience.mid')}</MenuItem>
                    <MenuItem value="senior-level">{t('filterSidebar.jobs.experience.senior')}</MenuItem>
                    <MenuItem value="executive">{t('filterSidebar.jobs.experience.executive')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="workplace-label">{t('filterSidebar.jobs.workplace.title')}</InputLabel>
                  <Select labelId="workplace-label" label={t('filterSidebar.jobs.workplace.title')} defaultValue="" sx={{ background: theme.palette.background.paper, borderRadius: 2 }}>
                    <MenuItem value="">{t('filterSidebar.jobs.workplace.any')}</MenuItem>
                    <MenuItem value="remote">{t('filterSidebar.jobs.workplace.remote')}</MenuItem>
                    <MenuItem value="hybrid">{t('filterSidebar.jobs.workplace.hybrid')}</MenuItem>
                    <MenuItem value="onsite">{t('filterSidebar.jobs.workplace.onsite')}</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    🎯 {t('filterSidebar.requiredSkills.title')}
                  </Typography>
                  {localRequiredSkills.length > 0 && (
                    <Chip
                      label={`${localRequiredSkills.length} skill${localRequiredSkills.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        background: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                </Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={localRequiredSkills}
                  onChange={(event, newValue) => {
                    console.log('Required skills changed:', newValue);
                    setLocalRequiredSkills(newValue || []);
                    setRequiredSkills(newValue || []);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    // Handle comma-separated input
                    if (reason === 'input' && newInputValue.includes(',')) {
                      const parts = newInputValue.split(',');
                      const newSkills = parts
                        .slice(0, -1) // Get all parts except the last one (which might be incomplete)
                        .map(skill => skill.trim())
                        .filter(skill => skill && !localRequiredSkills.includes(skill));
                      
                      if (newSkills.length > 0) {
                        const updatedSkills = [...localRequiredSkills, ...newSkills];
                        setLocalRequiredSkills(updatedSkills);
                        setRequiredSkills(updatedSkills);
                      }
                      
                      // Clear the input after processing comma
                      if (event && event.target && 'value' in event.target) {
                        (event.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                  renderTags={(value, getTagProps) =>
                    (value || []).map((option, index) => (
                      <Chip
                        variant="filled"
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                        sx={{
                          background: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          height: '32px',
                          borderRadius: '16px',
                          boxShadow: `0 10px 18px ${alpha(theme.palette.primary.main, 0.18)}`,
                          '&:hover': {
                            background: theme.palette.primary.dark,
                            boxShadow: `0 14px 24px ${alpha(theme.palette.primary.main, 0.24)}`,
                          },
                          '& .MuiChip-deleteIcon': {
                            color: theme.palette.primary.contrastText,
                            fontSize: '18px',
                            '&:hover': {
                              color: alpha(theme.palette.primary.contrastText, 0.85),
                            },
                          },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t('filterSidebar.requiredSkills.title')}
                      placeholder={t('filterSidebar.requiredSkills.placeholder')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: theme.palette.background.paper,
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: theme.palette.divider,
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.text.secondary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2,
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(0,0,0,0.6)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: theme.palette.primary.main,
                        }
                      }}
                    />
                  )}
                />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'text.primary' }}>
                    ⭐ {t('filterSidebar.condition.title')}
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedCondition}
                    onChange={(e) => setSelectedCondition(e.target.value)}
                    sx={{
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.secondary,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    }}
                  >
                    {conditions.map(condition => (
                      <MenuItem key={condition.value} value={condition.value}>
                        {condition.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Paper>
        </motion.div>
      </Box>

      {/* Brands Selection Popup */}
      <Dialog
        open={brandsPopupOpen}
        onClose={() => setBrandsPopupOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[10],
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: theme.palette.primary.contrastText,
          fontWeight: 'bold',
          fontSize: '1.25rem'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {is_job ? `🏢 ${t('filterSidebar.brands.dialog.selectCompanies')}` : `🏷️ ${t('filterSidebar.brands.dialog.selectBrands')}`}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {brands.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('filterSidebar.brands.dialog.noBrands')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
                {brands.map(brand => (
                  <FormControlLabel
                    key={brand}
                    control={
                      <Checkbox
                        checked={selectedBrands.has(brand)}
                        onChange={() => handleBrandToggle(brand)}
                        sx={{
                          color: 'text.secondary',
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {brand}
                      </Typography>
                    }
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 2,
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.06),
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setBrandsPopupOpen(false)}
            variant="outlined"
            sx={{
              borderColor: theme.palette.divider,
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                background: alpha(theme.palette.primary.main, 0.06),
              }
            }}
          >
            {t('filterSidebar.dialog.cancel')}
          </Button>
          <Button
            onClick={() => setBrandsPopupOpen(false)}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: theme.palette.primary.dark,
              }
            }}
          >
            {t('filterSidebar.dialog.done')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Search Modal */}
      <MapSearchNew
        open={mapSearchOpen}
        onClose={() => setMapSearchOpen(false)}
        onLocationSelect={(location) => {
          const newLocations = [...selectedMapLocations, location.name];
          setSelectedMapLocations(newLocations);
          // Update the customFilter5 with the new locations
          const newValue = newLocations.join(', ');
          setCustomFilter5(newValue);
        }}
        selectedLocations={selectedMapLocations}
        initialSearchQuery={customFilter5}
        isJobMode={isJobMode}
      />

      {/* AI Search with Image Modal */}
      <AISearchWithImageModal
        open={aiSearchOpen}
        onClose={() => setAiSearchOpen(false)}
        onSearch={async (imageFile, searchQuery) => {
          // The AI Search modal handles its own processing internally
          // This handler is called but the modal manages its own state
          console.log('AI Search initiated:', { imageFile, searchQuery });
        }}
      />
    </Drawer>
  );
};

export default FilterSidebar;
